import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import ProgressMeter from '../components/OwnerDiagnostic/ProgressMeter';
import WealthGapQuestion from '../components/WealthGapWizard/WealthGapQuestion';
import WealthGapResults from '../components/WealthGapWizard/WealthGapResults';

const GET_CURRENT_USER = gql`
  query GetCurrentUserForWealthGap {
    getCurrentUser {
      id
      readiness_goal_type
      readiness_goal_lump_sum
      readiness_goal_annual_income
      readiness_withdrawal_rate
      readiness_current_investable_assets
      readiness_include_illiquid
      readiness_gap_value
      readiness_gap_zone
      readiness_percent_to_goal
      readiness_last_updated_at
    }
  }
`;

const UPSERT_READINESS = gql`
  mutation UpsertPersonalFinancialReadiness(
    $goal_type: String
    $goal_lump_sum: Float
    $annual_income: Float
    $withdrawal_rate: Float
    $current_investable_assets: Float
    $include_illiquid: Boolean
  ) {
    upsertPersonalFinancialReadiness(
      goal_type: $goal_type
      goal_lump_sum: $goal_lump_sum
      annual_income: $annual_income
      withdrawal_rate: $withdrawal_rate
      current_investable_assets: $current_investable_assets
      include_illiquid: $include_illiquid
    ) {
      user {
        id
        readiness_goal_type
        readiness_goal_lump_sum
        readiness_goal_annual_income
        readiness_withdrawal_rate
        readiness_current_investable_assets
        readiness_include_illiquid
        readiness_gap_value
        readiness_gap_zone
        readiness_percent_to_goal
        readiness_last_updated_at
      }
      errors
    }
  }
`;

const SEND_ANALYTICS_EVENT = gql`
  mutation SendAnalyticsEvent($event_name: String!, $custom_props: JsonType) {
    sendAnalyticsEvent(event_name: $event_name, custom_props: $custom_props)
  }
`;

const InnerWizard = () => {
  const { data, loading, error } = useQuery(GET_CURRENT_USER, { fetchPolicy: 'cache-and-network' });
  const [sendEvent] = useMutation(SEND_ANALYTICS_EVENT);
  const [upsert, { loading: saving }] = useMutation(UPSERT_READINESS);
  const [step, setStep] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const user = data?.getCurrentUser || {};

  const initializedRef = useRef(false);
  const [values, setValues] = useState({
    goal_type: 'lump_sum',
    goal_lump_sum: 0,
    annual_income: 0,
    withdrawal_rate: 0.04,
    current_investable_assets: 0,
    include_illiquid: false,
  });

  // Initialize local form state once from server values to prevent resets while typing
  useEffect(() => {
    if (!initializedRef.current && user && user.id) {
      setValues({
        goal_type: user.readiness_goal_type || 'lump_sum',
        goal_lump_sum: user.readiness_goal_lump_sum || 0,
        annual_income: user.readiness_goal_annual_income || 0,
        withdrawal_rate: user.readiness_withdrawal_rate || 0.04,
        current_investable_assets: user.readiness_current_investable_assets || 0,
        include_illiquid: user.readiness_include_illiquid || false,
      });
      initializedRef.current = true;
    }
  }, [user]);

  const handleChange = useCallback((patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const track = useCallback(async (eventName, props) => {
    try { await sendEvent({ variables: { event_name: eventName, custom_props: props } }); } catch(e) {}
  }, [sendEvent]);

  const saveGoalStep = useCallback(async () => {
    try {
      await upsert({
        variables: {
          goal_type: values.goal_type,
          goal_lump_sum: values.goal_lump_sum,
          annual_income: values.annual_income,
          withdrawal_rate: values.withdrawal_rate,
        }
      });
    } catch(e) {}
  }, [upsert, values]);

  const saveAssets = useCallback(async (patch = {}) => {
    try {
      await upsert({
        variables: {
          current_investable_assets: patch.current_investable_assets != null ? patch.current_investable_assets : values.current_investable_assets,
          include_illiquid: patch.include_illiquid != null ? patch.include_illiquid : values.include_illiquid,
        }
      });
    } catch(e) {}
  }, [upsert, values]);

  const handleNext = useCallback(async () => {
    const next = Math.min(step + 1, 2);
    if (next === 2) {
      setShowResults(true);
    }
    setStep(next);
  }, [step]);

  const handleBack = useCallback(() => {
    if (showResults) {
      setShowResults(false);
      setStep(1);
      return;
    }
    setStep(Math.max(step - 1, 0));
  }, [step, showResults]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  if (showResults) {
    return (
      <WealthGapResults user={user} onBack={handleBack} track={track} />
    );
  }

  const indexWithin = step + 1;

  return (
    <div>
      <div className="px-4 md:px-0 max-w-3xl mx-auto mt-2">
        <ProgressMeter current={step} total={2} />
      </div>
      <WealthGapQuestion
        step={step}
        indexWithinSection={indexWithin}
        values={values}
        onChange={handleChange}
        onSaveGoal={saveGoalStep}
        onSaveAssets={saveAssets}
        onNext={handleNext}
        onBack={handleBack}
        saving={saving}
        track={track}
      />
    </div>
  );
};

const WealthGapWizardContainer = () => (
  <ApolloProvider client={client}>
    <InnerWizard />
  </ApolloProvider>
);

export default WealthGapWizardContainer;


